import { getCSRFParam, getCSRFToken } from '@aytm/lib/common/rails-post-fetch'

// --- Type tests and comparison ---------------------------------------------------------------------

export const is = Type => target => (null != target && target.constructor === Type) || target instanceof Type

export const collator = new Intl.Collator(undefined, { sensitivity: 'accent' })

export const casecmp = (a, b) =>
  is(String)(a) && is(String)(b) ? 0 == collator.compare(a, b) : is(Array)(a) && is(Array)(b) ? arraycmp(a, b) : a == b

export function arraycmp(a, b) {
  return a.length == b.length && a.reduce((res, v, i) => res && casecmp(v, b[i]), true)
}

// --- Reading and rewriting nested objects ----------------------------------------------------------

export const getIn = (path = [], obj, options = {}) => {
  if (obj == undefined || null === obj) return
  // A null path reaches here as null, not as the default — the legacy callers relied on it
  // answering undefined rather than throwing on `.length`.
  if (null == path) return
  if (!path.length) return obj

  let node = obj
  for (let index = 0; index < path.length; index++) {
    const p = path[index]
    const key = Object.keys(options).find(k => p.indexOf(k))
    if (key) {
      node = node[p.replace(key, options[key])]
    } else node = node[p]

    if (index + 1 < path.length && 'object' !== typeof node) return
  }
  return node
}

export const updateIn = (path = [], mutator, obj) => {
  if (!path.length) return obj
  const lastIndex = path.length - 1
  let node = obj
  for (const [index, p] of path.entries()) {
    if (lastIndex != index && 'object' != typeof node[p]) {
      node[p] = {}
    } else if (lastIndex == index) {
      node[p] = mutator(node[p])
    }
    node = node[p]
  }
  return obj
}

export const setIn = (path = [], value, obj) => updateIn(path, () => value, obj)

export const assocPathRecur = (path, value, obj = {}, index = 0) =>
  Object.assign({}, is(Object, obj) ? obj : {}, {
    [path[index]]: index == path.length - 1 ? value : assocPathRecur(path, value, obj[path[index]], index + 1)
  })

export const assocPath = (path, value) => obj => assocPathRecur(path, value, obj, 0)

export const omit = (properties = [], obj) =>
  Object.keys(obj).reduce(
    (omitted, property) => (properties.includes(property) ? omitted : ((omitted[property] = obj[property]), omitted)),
    {}
  )

export const pick = (properties = [], obj) =>
  properties.reduce((picked, property) => ((picked[property] = obj[property]), picked), {})

// --- Collections -----------------------------------------------------------------------------------

export const uniq = array => array.filter((element, index, array) => array.indexOf(element) == index)

export const getSize = obj => Math.round(JSON.stringify(obj).length / 1024)

// --- Numbers ---------------------------------------------------------------------------------------

export const round = (v, d = 0) => {
  const tenPow = Math.pow(10, d)
  return Math.round(v * tenPow) / tenPow
}

export const toPrecision = precision => {
  const precisionMod = Math.pow(10, precision)
  return value => Math.round(precisionMod * value) / precisionMod
}

// `stricked` keeps the fixed number of decimals instead of collapsing a value that merely
// rounds to an integer: formatAmounts(2.001) is "2", formatAmounts(2.001, null, 2, true) is
// "2.00". The collapse tests the rounded string, not the raw value, so 2.001 and 2 agree.
export const formatAmounts = (r, attr, n = 2, stricked = false) => {
  let res = 0

  if (r != undefined && null !== r) {
    if (attr == undefined || null === attr) {
      if ('string' == typeof r) return r
      res = round(r, n).toFixed(n)
      if (Number(res) == Math.round(r) && !stricked) return String(Math.round(r))
    }

    const _attr = Array.isArray(attr) ? attr.find(a => r[a] != undefined && null !== r[a]) : attr

    if (_attr != undefined && null !== _attr) {
      if (r[_attr] == undefined || null === r[_attr]) return (stricked && (0).toFixed(n)) || '0'
      if ('string' == typeof r[_attr]) return r[_attr]
      res = round(r[_attr], n).toFixed(n)
      if (Number(res) == Math.round(r[_attr]) && !stricked) return String(Math.round(r[_attr]))
    }
  }

  res = (0 === Number(res) && '0') || res || '0'
  return (stricked && Number(res).toFixed(n)) || res
}

// --- Text the page draws as markup -----------------------------------------------------------------

// A missing value reads as empty text, so it never prints as "null" or throws on a string method; the
// coffee page.lib calls these same two, so both sides agree.
export const encodeText = (text, brack = true) => {
  let newText = null != text ? String(text) : ''
  newText = newText.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  return brack ? newText.replace(/\[/g, '&lbrack;').replace(/]/g, '&rbrack;') : newText
}

// A label that arrives as plain text -- a demographic trait's name, say, which is not encoded at
// rest the way question text is -- and is drawn as markup. Ids and positions are not prose and
// keep their own type.
export const encodeLabel = value => ('string' == typeof value ? encodeText(value) : value)

export const decodeText = (text, brack = true) => {
  let newText = null != text ? String(text) : ''
  newText = brack ? newText.replace(/&lbrack;/g, '[').replace(/&rbrack;/g, ']') : newText
  return newText.replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&amp;/g, '&')
}

// --- Text cut to a stored length ----------------------------------------------------------------

const graphemes =
  'function' == typeof Intl.Segmenter ? new Intl.Segmenter(undefined, { granularity: 'grapheme' }) : null

// A length cap counts UTF-16 units, so a bare slice can end inside a character: half an emoji (a lone
// surrogate the save then carries), an emoji without its skin tone or the rest of its ZWJ family, a
// letter without its accent. The cut keeps whole user-perceived characters only. Browsers without
// Intl.Segmenter fall back to dropping a split surrogate pair, the one break that corrupts the string.
export const clipText = (value, limit) => {
  if (!value || value.length <= limit) return value

  if (graphemes) {
    let end = 0
    for (const { segment } of graphemes.segment(value)) {
      if (end + segment.length > limit) break
      end += segment.length
    }
    return value.slice(0, end)
  }

  const clipped = value.slice(0, limit)
  return /[\uD800-\uDBFF]$/.test(clipped) ? clipped.slice(0, -1) : clipped
}

export const escapeRegExp = string => string.replace(/[$()*+.?[\\\]^{|}]/g, '\\$&')

// The plural form defaults to the singular with an `s`, which is what most callers would pass
// anyway; an irregular one is given explicitly.
export const pluralize = (n, one, many = `${one}s`) => (1 === n ? one : many)

// The comments-count title every surface draws. The form follows the count, while the number
// drawn is the caller's own (a header rounds it, a table cell shows the raw count). A
// zero count has nothing to open, so it drops the "View"; a surface with its own empty wording
// never calls this at zero.
export const customAnswersTitle = (count, formatted = count) =>
  `${0 < Number(count) ? 'View ' : ''}${formatted} custom ${pluralize(Number(count), 'answer')}`

// --- Identifiers -----------------------------------------------------------------------------------

export const generateUID = () => {
  const timestamp = (Number(Date.now()) - Number(new Date(2017, 9, 1))).toString(36)
  const number = Math.round(Math.random() * 100000).toString(36)

  return timestamp + number
}

// --- Errors ----------------------------------------------------------------------------------------

export const createErrorType = (name, initFunction) => {
  function E(message, data = null) {
    this.message = message
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, this.constructor)
    } else {
      this.stack = new Error('stack').stack
    }
    if (null != data) {
      this.data = data
    }
    initFunction && initFunction.apply(this, arguments)
  }
  E.prototype = Object.create(Error.prototype)
  E.prototype.name = name
  E.prototype.constructor = E
  return E
}

// --- Requests --------------------------------------------------------------------------------------

export const getStatUrl = (url, skip = false) => {
  const sp = document.location.pathname.split('/')
  return sp
    .slice(0, 3)
    .concat([url])
    .concat((skip && []) || [sp[4]])
    .filter(function (v) {
      return null !== v && 'undefined' !== typeof v
    })
    .join('/')
}

// Rails refuses a non-GET without the token, under the param name the page itself names. One home
// for it rather than one per api-call module: a copy that drifted would be refused by the server
// rather than caught by a test. Shared by the JSON posts and by the hidden-form submit alike.
export const addCSRF = data => {
  data[getCSRFParam()] = getCSRFToken()

  return data
}
